import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, CheckCircle2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLoading } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS_CLR = {
  PENDING: "bg-amber-100 text-amber-700",
  APPROVED: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-red-100 text-red-700",
  CANCELLED: "bg-slate-100 text-slate-600",
};
const TYPE_LABEL = {
  MISSING_CLOCK_IN: "Einstempeln fehlt", MISSING_CLOCK_OUT: "Ausstempeln fehlt",
  MISSING_BREAK: "Pause fehlt", WRONG_BREAK: "Pause falsch", WRONG_SITE: "Falsche Baustelle",
  WRONG_TIME: "Falsche Zeit", OTHER: "Sonstiges",
};
const fmt = (v) => {
  if (!v) return null;
  try { return new Date(v).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
};

export default function CorrectionRequests() {
  const { user } = useAuth();
  const isAdmin = ["COMPANY_OWNER", "COMPANY_ADMIN", "MANAGER"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("PENDING");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    const qs = status ? `?status=${status}` : "";
    api.get(`/employer/time/correction-requests${qs}`)
      .then((r) => setRows(r.data.requests || []))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    const review_comment = window.prompt("Kommentar (optional):") ?? undefined;
    setBusy(id);
    try { await api.post(`/employer/time/correction-requests/${id}/approve`, { review_comment }); toast.success("Genehmigt"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy("");
  };
  const reject = async (id) => {
    const review_comment = window.prompt("Grund der Ablehnung (optional):") ?? undefined;
    setBusy(id);
    try { await api.post(`/employer/time/correction-requests/${id}/reject`, { review_comment }); toast.success("Abgelehnt"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy("");
  };

  if (!rows) return <PageLoading />;

  return (
    <div data-testid="correction-requests">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Link to="/app/employer/time" className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="corr-back"><ArrowLeft className="h-4 w-4" /> Zurück</Link>
        <h1 className="ml-2 flex items-center gap-2 font-heading text-2xl font-extrabold text-navy"><ClipboardCheck className="h-6 w-6 text-cyan-dark" /> Korrekturanfragen</h1>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="ml-auto rounded-lg border border-slate-200 px-2 py-2 text-sm" data-testid="corr-status-filter">
          <option value="PENDING">Offen</option>
          <option value="APPROVED">Genehmigt</option>
          <option value="REJECTED">Abgelehnt</option>
          <option value="">Alle</option>
        </select>
      </div>
      <p className="mb-6 text-sm text-slate-500">Von Mitarbeitern über die App beantragte Zeitkorrekturen. Prüfung im Vier-Augen-Prinzip — die Original-Erfassungen bleiben unverändert.</p>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="corr-table">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["Datum", "Mitarbeiter", "Typ", "Beantragt", "Grund", "Status"].map((c) => <th key={c} className="px-4 py-3">{c}</th>)}<th /></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={7} className="p-10 text-center text-slate-400" data-testid="corr-empty">Keine Korrekturanfragen.</td></tr> :
              rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 align-top hover:bg-slate-50" data-testid={`corr-row-${r.id}`}>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">{r.work_date}</td>
                  <td className="px-4 py-3"><div className="font-medium text-navy">{r.worker_name}</div><div className="text-xs text-slate-400">{r.worker_id}</div></td>
                  <td className="px-4 py-3 text-slate-600">{TYPE_LABEL[r.request_type] || r.request_type}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {r.requested_clock_in && <div>Ein: {fmt(r.requested_clock_in)}</div>}
                    {r.requested_clock_out && <div>Aus: {fmt(r.requested_clock_out)}</div>}
                    {r.requested_break_start && <div>Pause: {fmt(r.requested_break_start)}–{fmt(r.requested_break_end)}</div>}
                    {!r.requested_clock_in && !r.requested_clock_out && !r.requested_break_start && <span className="text-slate-400">—</span>}
                  </td>
                  <td className="max-w-[220px] px-4 py-3 text-xs text-slate-500">{r.reason || r.comment || "—"}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_CLR[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                    {r.review_comment && <div className="mt-1 text-[10px] text-slate-400">{r.review_comment}</div>}</td>
                  <td className="px-4 py-3 text-right">
                    {r.status === "PENDING" && isAdmin ? (
                      <div className="flex justify-end gap-1">
                        <button disabled={busy === r.id} onClick={() => approve(r.id)} className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" data-testid={`corr-approve-${r.id}`}>Genehmigen</button>
                        <button disabled={busy === r.id} onClick={() => reject(r.id)} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid={`corr-reject-${r.id}`}>Ablehnen</button>
                      </div>
                    ) : r.status === "APPROVED" ? <CheckCircle2 className="ml-auto h-4 w-4 text-emerald-500" />
                      : r.status === "REJECTED" ? <X className="ml-auto h-4 w-4 text-red-500" /> : null}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
